import React, { useCallback } from 'react';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import useClassNames from 'helpers/hooks/useClassNames';
import useControllableState from 'helpers/hooks/useControllable';
import { QuantitySelectorProps } from './types';

const QuantitySelector = ({
  value: valueProp,
  defaultValue = 0,
  minValue = -Infinity,
  maxValue = Infinity,
  disabled = false,
  className: classNameProp,
  onChange,
}: QuantitySelectorProps) => {
  const [value, setValue] = useControllableState(valueProp, defaultValue);

  const className = useClassNames([
    'flex w-fit items-center rounded-sm border-2 border-stroke-primary transition',
    !disabled ? 'hover:border-stroke-secondary' : '',
    disabled ? 'cursor-not-allowed' : 'bg-white text-primary',
    classNameProp,
  ]);

  const normalizeValue = useCallback(
    (val: number) => Math.max(minValue, Math.min(maxValue, val)),
    [minValue, maxValue],
  );

  const handleChange = useCallback(
    (val: number) => {
      if (disabled) return;

      const normalizedValue = normalizeValue(val);

      setValue(normalizedValue);
      onChange?.(normalizedValue);
    },
    [setValue, normalizeValue, onChange, disabled],
  );

  return (
    <div className={className}>
      <button
        disabled={value <= minValue || disabled}
        onClick={() => handleChange(value - 1)}
        className="cursor-pointer self-stretch border-r-2 border-stroke-primary p-10 disabled:cursor-not-allowed disabled:text-feedback-disabled"
      >
        <MinusIcon width={20} strokeWidth={1.5} />
      </button>
      <span className="body-text-small w-40 text-center font-medium">{value}</span>
      <button
        disabled={value >= maxValue || disabled}
        onClick={() => handleChange(value + 1)}
        className="cursor-pointer self-stretch border-l-2 border-stroke-primary p-10 disabled:cursor-not-allowed disabled:text-feedback-disabled"
      >
        <PlusIcon width={20} strokeWidth={1.5} className="fill-icon-primary" />
      </button>
    </div>
  );
};

export default QuantitySelector;
